import { useEffect, useState } from "react";
import { Mail, Instagram, MessageCircle, Clock, Loader2, Send } from "lucide-react";
import { toast } from "sonner";
import PageHeader from "../components/PageHeader";
import { Reveal } from "../components/Reveal";
import { BRAND, whatsappLink } from "../data";
import { submitContact } from "../lib/api";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    document.title = "Contact — JOBLSX";
  }, []);

  const update = (k) => (e) => {
    setForm((f) => ({ ...f, [k]: e.target.value }));
    setErrors((er) => ({ ...er, [k]: undefined }));
  };

  const validate = () => {
    const er = {};
    if (!form.name.trim()) er.name = "Please enter your name.";
    if (!form.email.trim()) er.email = "Please enter your email.";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) er.email = "Enter a valid email.";
    if (!form.message.trim()) er.message = "Please write a message.";
    setErrors(er);
    return Object.keys(er).length === 0;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);
    try {
      await submitContact(form);
      toast.success("Message sent! We'll get back to you shortly.");
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch {
      toast.error("Could not send your message. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full rounded-xl bg-[#0a0a0a] border border-white/10 px-4 py-3 text-white placeholder-slate-500 outline-none transition-colors focus:border-blue-500 focus:ring-1 focus:ring-blue-500";

  const infos = [
    { icon: Mail, label: "Email", value: BRAND.email, href: `mailto:${BRAND.email}`, testid: "contact-email" },
    { icon: MessageCircle, label: "WhatsApp", value: BRAND.whatsappNumber, href: whatsappLink(), testid: "contact-whatsapp" },
    { icon: Instagram, label: "Instagram", value: `@${BRAND.instagram}`, href: BRAND.instagramUrl, testid: "contact-instagram" },
  ];

  return (
    <div>
      <PageHeader
        overline="Contact"
        title="Let's talk."
        subtitle="Reach out through the form, WhatsApp, or email. We respond fast and keep everything confidential."
      />

      <section className="pb-24 pt-8">
        <div className="mx-auto max-w-7xl px-6 md:px-12">
          <div className="grid gap-10 lg:grid-cols-5">
            {/* info */}
            <Reveal className="lg:col-span-2">
              <div className="space-y-4">
                {infos.map((it) => (
                  <a
                    key={it.label}
                    href={it.href}
                    target={it.href.startsWith("http") ? "_blank" : undefined}
                    rel="noopener noreferrer"
                    data-testid={it.testid}
                    className="flex items-center gap-4 rounded-2xl border border-white/10 bg-white/[0.03] p-5 transition-colors duration-300 hover:border-blue-500/50"
                  >
                    <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-blue-600/15 text-blue-400">
                      <it.icon className="h-5 w-5" />
                    </div>
                    <div>
                      <p className="text-xs uppercase tracking-wider text-slate-500">{it.label}</p>
                      <p className="font-medium text-white">{it.value}</p>
                    </div>
                  </a>
                ))}
                <div className="flex items-center gap-4 rounded-2xl border border-white/10 bg-white/[0.03] p-5">
                  <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-blue-600/15 text-blue-400">
                    <Clock className="h-5 w-5" />
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-wider text-slate-500">Business Hours</p>
                    <p className="font-medium text-white">{BRAND.hours}</p>
                  </div>
                </div>
              </div>
            </Reveal>

            {/* form */}
            <Reveal delay={0.1} className="lg:col-span-3">
              <form onSubmit={submit} className="rounded-3xl border border-white/10 bg-white/[0.02] p-8 md:p-10" data-testid="contact-form" noValidate>
                <div className="grid gap-5 sm:grid-cols-2">
                  <div>
                    <input className={inputCls} placeholder="Full name" value={form.name} onChange={update("name")} data-testid="contact-name-input" />
                    {errors.name && <p className="mt-1 text-xs text-red-400" data-testid="contact-name-error">{errors.name}</p>}
                  </div>
                  <div>
                    <input className={inputCls} type="email" placeholder="Email address" value={form.email} onChange={update("email")} data-testid="contact-email-input" />
                    {errors.email && <p className="mt-1 text-xs text-red-400" data-testid="contact-email-error">{errors.email}</p>}
                  </div>
                </div>
                <input className={`${inputCls} mt-5`} placeholder="Subject (optional)" value={form.subject} onChange={update("subject")} data-testid="contact-subject-input" />
                <div className="mt-5">
                  <textarea className={inputCls} rows={6} placeholder="How can we help?" value={form.message} onChange={update("message")} data-testid="contact-message-input" />
                  {errors.message && <p className="mt-1 text-xs text-red-400" data-testid="contact-message-error">{errors.message}</p>}
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  className="mt-6 flex w-full items-center justify-center gap-2 rounded-full bg-blue-600 py-4 font-medium text-white shadow-[0_0_24px_rgba(37,99,235,0.4)] transition-all duration-300 hover:bg-blue-700 hover:shadow-[0_0_32px_rgba(37,99,235,0.6)] disabled:opacity-60"
                  data-testid="contact-submit-button"
                >
                  {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <>Send Message <Send className="h-4 w-4" /></>}
                </button>
              </form>
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
}
